import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Collapse, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { space } from '@viq-admin/ui';
import { ENRICHMENT_COPY } from './contactEnrichmentCopy';

const { Text } = Typography;

/**
 * One contact-enrichment candidate value for a lead, as returned by the
 * viq-backend contact-enrichment endpoints (frozen wire shape). `confidence`
 * and `verification` come from the sourcing provider; `catchAll` flags a
 * domain that accepts all mailboxes (so a `valid` verification still can't be
 * trusted to reach a real inbox); `inferred` flags a pattern-guessed value
 * with no verification signal at all. `rejectedAt` is set once an operator
 * has dismissed it — rejected candidates are never deleted, just hidden.
 */
export interface EnrichmentCandidate {
  id: string;
  field: 'email' | 'phone';
  value: string;
  source: string;
  confidence?: number;
  verification?: 'valid' | 'invalid' | 'probable' | 'unknown';
  catchAll?: boolean;
  inferred?: boolean;
  /** A citation the sourcing agent found for this value: the page it came
   *  from and the exact snippet supporting it. `null`/absent for provider
   *  lookups and pattern guesses with nothing to cite — those keep the
   *  `hypothesis`/`inferred` tag instead of a link. */
  evidence?: { url: string; snippet: string } | null;
  fetchedAt: string;
  rejectedAt: string | null;
  /**
   * fix/enrichment-matches-current: server-computed, format-blind "already
   * matches the person's current value" — e.g. a stored phone
   * "(859) 707-8805" and a candidate "+18597078805" are the same digits, so
   * this is `true` even though the raw strings differ. Optional so an older
   * backend (deploy skew) degrades gracefully: absent falls back to the
   * previous raw `value === person[field]` comparison below, never to a
   * crash or a silently-wrong always-false.
   */
  matchesCurrent?: boolean;
}

/**
 * What one enabled layer of the ladder did on the last Enrich call — the
 * backend's `levels` summary. A layer with NO entry is not turned on.
 * `reason` is a machine key mapped to words in ENRICHMENT_COPY.levelSkipped;
 * an unknown key renders as a plain "Skipped" so a newer backend never breaks
 * an older panel.
 */
export type EnrichmentRunLevel =
  | {
      provider: string;
      status: 'ran';
      candidates: number;
      fields: Array<'email' | 'phone'>;
      durationMs: number;
      /** Answers the layer produced but could not prove or did not want. */
      dropped?: number;
      budgetExhausted?: true;
      /** Agent only: how many web searches it made. */
      searches?: number;
      /** Agent only: hostnames of the pages it read (older backends). */
      pagesRead?: string[];
      /** Agent only: the pages it read, as full http(s) links the operator can follow. */
      pages?: string[];
      /** Answers the layer could not prove but kept, listed with the candidates for a human to review. */
      needsReview?: number;
      /** Fields the layer asked for that arrive LATER (a webhook delivery): "nothing found" is not yet its answer. */
      awaiting?: Array<'email' | 'phone'>;
    }
  | { provider: string; status: 'skipped'; reason: string }
  | { provider: string; status: 'failed'; reason: string; cause?: 'rate_limited' | 'unavailable' | 'timeout' | 'error' };

/** One site the domain lookup weighed, and what it concluded about it. */
export interface EnrichmentDomainConsidered {
  domain: string;
  /** The homepage that was read for it. */
  url: string;
  chosen: boolean;
  reason: string;
}

/**
 * How the employer's web domain was settled on a run — what Apollo and the
 * email guesses depend on, with the evidence behind it. Absent on a run from
 * before it was recorded.
 */
export interface EnrichmentDomainOutcome {
  status: 'known' | 'resolved' | 'resolved_weak' | 'agent' | 'not_found' | 'unavailable';
  host?: string;
  /** The page the domain was judged on. */
  sourceUrl?: string;
  reason?: string;
  confidence?: 'high' | 'medium' | 'low';
  /** Where company addresses are published, when that differs from `host`, and the page that shows it. */
  emailDomain?: string;
  emailDomainSeenAt?: string;
  considered?: EnrichmentDomainConsidered[];
}

/**
 * The persisted summary of one enrich call (viq-backend migration 053).
 * Counts, field names, reasons, durations and links to the public pages the
 * run read — the backend never stores a candidate value, quote, search query
 * or page text here.
 */
export interface EnrichmentRunSummary {
  v: number;
  outcome: 'ok' | 'refused_thin_identity' | 'provider_error';
  forced: boolean;
  explicit: string[] | null;
  wanted: Array<'email' | 'phone'>;
  found: { email: number; phone: number };
  durationMs: number;
  levels: EnrichmentRunLevel[];
  /** How the employer domain was settled, with evidence. */
  domain?: EnrichmentDomainOutcome;
  /** Guessed addresses offered because no real email was found. */
  guessed?: number;
  /** The company's own site was read for its main phone line: the pages that loaded, and how many numbers were offered. */
  companyPhone?: { found: number; checked: string[] };
}

/** One past enrich call. `summary` is null for a run from before run
 *  summaries existed, or one whose summary aged out of retention. */
export interface EnrichmentRunHistoryEntry {
  at: string;
  by: string;
  status: string;
  summary: EnrichmentRunSummary | null;
}

/** The full current enrichment picture for a lead: candidates found so far,
 *  any in-flight async provider requests, and the person's LIVE email/phone
 *  (used to tell an "already matches" candidate from one still awaiting a
 *  decision). */
export interface EnrichmentState {
  candidates: EnrichmentCandidate[];
  requests: Array<{ provider: string; fields: string[]; status: string; requestedAt: string }>;
  person: { email: string; phone: string };
  /** Set on a successful response when the backend skipped Apollo for a
   *  reason other than "found nothing" — today only `'daily_ceiling'`,
   *  rendered as the same out-of-credits notice a 429 produces, just reached
   *  via the non-error path. */
  apolloSkipped?: string;
  /** Present on the response to an Enrich click only (never on a plain read):
   *  what each enabled layer did. Absent from an older backend. */
  levels?: EnrichmentRunLevel[];
  /** The lead's recent enrich calls, newest first (up to 10) — what lets the
   *  page show "what ran" after a reload. Absent from an older backend. */
  runs?: EnrichmentRunHistoryEntry[];
}

/**
 * Shell-provided contact-enrichment access. OPTIONAL on LeadDetailApi by
 * design: the super-admin console supplies it, the customer portal does not
 * — which is what keeps contact enrichment out of the portal at the UI
 * layer. `enrich` re-runs the provider ladder in one click — no spend
 * confirmation and no "enriched recently" block — and rejects with `{status:
 * 422}` when the identity is too thin to spend a lookup on. `rejectCandidate` marks one candidate dismissed — it does not
 * delete it, so a rejection can always be reviewed later.
 */
export interface LeadEnrichmentApi {
  fetchEnrichment: (personId: string) => Promise<EnrichmentState>;
  /** `providers`, when passed, scopes the run to just those providers. Used
   *  by "Check Apollo too" to re-run only Apollo without repeating the rest
   *  of the ladder. */
  enrich: (personId: string, opts?: { providers?: string[] }) => Promise<EnrichmentState>;
  rejectCandidate: (personId: string, candidateId: string) => Promise<void>;
}

export interface ContactEnrichmentPanelProps {
  personId: string;
  api: LeadEnrichmentApi;
  /**
   * Called ONLY with the field and value an operator accepted. This panel
   * never writes to the person itself — that is the never-write-to-persons
   * boundary between "found a candidate" and "changed the record": the
   * caller (LeadDetailBody) decides what "use" means (prefilling the shared
   * edit form's draft and switching it into edit mode) and the actual write
   * still goes through that form's own explicit Save.
   */
  onUseValue: (field: 'email' | 'phone', value: string) => void;
}

const FIELD_ORDER: Array<'email' | 'phone'> = ['email', 'phone'];
const FIELD_LABEL: Record<'email' | 'phone', string> = { email: 'Email', phone: 'Phone' };

/** Round 2 (human-readable source tags, 2026-09-23): the curated label for a
 *  candidate's `source`, keyed off the values the backend actually sends
 *  (`agent`, `capture`, `prior_capture`, `apollo`). This is now the ONLY
 *  source chip a candidate row renders — the raw source key itself is never
 *  shown on screen. Anything not listed here (a raw provider name like
 *  `clearbit`, or a test's `stub`) falls through to `humanizeSourceKey`
 *  below instead — never the bare snake_case key. */
const SOURCE_LABEL: Partial<Record<string, string>> = {
  agent: ENRICHMENT_COPY.sourceAgent,
  capture: ENRICHMENT_COPY.sourceCapture,
  prior_capture: ENRICHMENT_COPY.sourcePriorCapture,
  apollo: ENRICHMENT_COPY.sourceApollo,
  company_site: ENRICHMENT_COPY.sourceCompanySite,
  agent_unverified: ENRICHMENT_COPY.sourceAgentUnverified,
};

/** Fallback for a `source` value with no curated `SOURCE_LABEL` entry:
 *  underscores become spaces and every word is title-cased —
 *  `'stub'` -> `'Stub'`, `'some_other_source'` -> `'Some Other Source'` —
 *  so an unrecognized provider key still reads as a label, never as a raw
 *  code identifier. `filter(Boolean)` guards a leading/trailing/doubled
 *  underscore from producing an empty word and a stray double space. */
function humanizeSourceKey(source: string): string {
  return source
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** The one source chip's text for a candidate: the curated label when one
 *  exists, otherwise the humanized raw key. Shared by the active-candidate
 *  row and the rejected-group row so neither ever falls back to printing
 *  the bare `source` string. */
function sourceChipLabel(source: string): string {
  return SOURCE_LABEL[source] ?? humanizeSourceKey(source);
}

/** pseudo-URLs (`viq:*`, `apollo:*`) are provenance KEYS, not destinations —
 *  e.g. `viq:prior-capture`, `viq:capture`. Only a real http(s) URL is safe
 *  (and useful) to render as a clickable link; everything else shows its
 *  snippet alone, with no link element and no pseudo-URL text on screen at
 *  all. Also front-runs the parked I3 gate slice — an eventual allowlist
 *  over which http(s) hosts get linked — by keeping the link path scoped to
 *  http(s) already. */
function isNavigableEvidenceUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

/** `https://www.fluvius.be/nl/over-ons` → `fluvius.be/nl/over-ons`, shortened — enough to see where a link goes. */
function linkLabel(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    const label = `${u.hostname.replace(/^www\./, '')}${path}`;
    return label.length > 48 ? `${label.slice(0, 47)}…` : label;
  } catch {
    return url;
  }
}

/** A link out to a page the backend read. http(s) only; opens in a new tab with no `window.opener` handle. */
function PageLink({ url }: { url: string }) {
  if (!isNavigableEvidenceUrl(url)) return null;
  return (
    <Typography.Link href={url} target="_blank" rel="noopener noreferrer" title={url}>
      {linkLabel(url)}
    </Typography.Link>
  );
}

const PENDING_POLL_MS = 15_000;
/** A delivery still pending after this long is not coming soon: stop polling; reloading the page still re-reads it. */
const POLL_WINDOW_MS = 30 * 60_000;

/** Is a webhook delivery pending AND recent enough to be worth polling for? */
function hasFreshPending(state: EnrichmentState | undefined): boolean {
  return (state?.requests ?? []).some(
    (r) => r.status === 'pending' && Date.now() - new Date(r.requestedAt).getTime() < POLL_WINDOW_MS,
  );
}

/** `enrich()` rejections carry `{status, message?}` — the shape the console's
 *  wrapper normalizes its thrown HttpError into for this API. Any other
 *  shape (network error, etc.) falls through to the generic toast below. */
function errorStatus(err: unknown): { status?: number; message?: string } {
  const e = err as { status?: number; message?: string } | null | undefined;
  return { status: e?.status, message: e?.message };
}

/** Round 2 (provenance polish, 2026-09-23): the tooltip title shared by the
 *  tier chip and the inferred/hypothesis tag — the exact percentage, moved
 *  off the visible tag and into hover. */
function confidenceTooltipTitle(confidence: number): string {
  return ENRICHMENT_COPY.confidenceTooltip(Math.round(confidence * 100));
}

/** Confidence tier word for the non-inferred tier chip. Boundaries are
 *  inclusive at the top of each tier: >= 0.85 Strong, >= 0.6 Good, else Weak. */
function confidenceTier(confidence: number): string {
  if (confidence >= 0.85) return ENRICHMENT_COPY.tierStrong;
  if (confidence >= 0.6) return ENRICHMENT_COPY.tierGood;
  return ENRICHMENT_COPY.tierWeak;
}

/** Priority when more than one flag applies: an invalid verifier result
 *  outranks a catch-all domain, which outranks a bare pattern guess — each
 *  is a strictly stronger reason to distrust the value than the next. */
function candidateStateTag(candidate: EnrichmentCandidate) {
  // An answer the AI could not prove: never shown as a finding, always as a lead to check.
  if (candidate.source === 'agent_unverified') {
    return <Tag color="warning">{ENRICHMENT_COPY.needsReview}</Tag>;
  }
  if (candidate.verification === 'invalid') {
    return <Tag color="error">{ENRICHMENT_COPY.invalidWarning}</Tag>;
  }
  if (candidate.catchAll) {
    return <Tag color="warning">{ENRICHMENT_COPY.catchAllWarning}</Tag>;
  }
  if (candidate.inferred) {
    const hypothesisTag = <Tag color="processing">{ENRICHMENT_COPY.hypothesis}</Tag>;
    // An inferred candidate never gets the Strong/Good/Weak tier chip (its
    // hypothesis tag already IS the tier signal) — but when there's a
    // numeric confidence to show, the same tooltip pattern still applies so
    // hovering the hypothesis tag reveals the exact percentage too.
    if (candidate.confidence == null) return hypothesisTag;
    return <Tooltip title={confidenceTooltipTitle(candidate.confidence)}>{hypothesisTag}</Tooltip>;
  }
  return null;
}

function CandidateRow({
  candidate,
  isMatch,
  onUse,
  onReject,
}: {
  candidate: EnrichmentCandidate;
  isMatch: boolean;
  onUse: (field: 'email' | 'phone', value: string) => void;
  onReject: (candidateId: string) => void;
}) {
  return (
    <div style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Space size={4} wrap>
          <Text>{candidate.value}</Text>
          {/* Round 2 (human-readable source tags, 2026-09-23): exactly ONE
              source chip — the raw source key is never rendered. Previously
              a raw `<Tag>{candidate.source}</Tag>` plus an optional friendly
              `<Text>` alongside it; the friendly label now IS the chip. */}
          <Tag>{sourceChipLabel(candidate.source)}</Tag>
          {/* Round 2 (provenance polish, 2026-09-23): tier word visible,
              exact percentage moved into the hover tooltip. Inferred
              candidates never get this chip — their hypothesis tag (below,
              via candidateStateTag) is the tier signal instead, and carries
              its own tooltip when confidence is numeric. */}
          {!candidate.inferred && candidate.source !== 'agent_unverified' && candidate.confidence != null && (
            <Tooltip title={confidenceTooltipTitle(candidate.confidence)}>
              <Tag>{confidenceTier(candidate.confidence)}</Tag>
            </Tooltip>
          )}
          {/* An already-matching candidate is a derived confirmation, never
              something a human "accepted" — so it gets its own tag instead of
              the invalid/catch-all/hypothesis state (which no longer matters:
              the value is already on the record either way) and no actions. */}
          {isMatch ? <Tag color="success">{ENRICHMENT_COPY.alreadyMatches}</Tag> : candidateStateTag(candidate)}
        </Space>
        {!isMatch && (
          <Space size={4}>
            <Button size="small" onClick={() => onUse(candidate.field, candidate.value)}>
              {ENRICHMENT_COPY.useValue}
            </Button>
            <Button size="small" onClick={() => onReject(candidate.id)}>
              {ENRICHMENT_COPY.reject}
            </Button>
          </Space>
        )}
      </Space>
      {/* A citation for this value: the exact snippet, plus a link out to
          read it in context — but ONLY when there's somewhere to actually
          navigate to. `evidence.url` is a real http(s) URL for the agent
          provider's web citations; for capture/prior-capture provenance it's
          a pseudo-URL like `viq:prior-capture` (a key, not a destination), so
          those rows show the snippet alone — no link element, and the
          pseudo-URL text itself is never printed either.
          `noopener noreferrer` on an operator-facing external link is
          clickjacking/tab-nabbing hygiene, not optional — the linked page
          never gets a `window.opener` handle. */}
      {candidate.evidence && (
        <Space size={4} wrap style={{ paddingLeft: space.md }}>
          <Text type="secondary">{candidate.evidence.snippet}</Text>
          {isNavigableEvidenceUrl(candidate.evidence.url) && (
            <Typography.Link href={candidate.evidence.url} target="_blank" rel="noopener noreferrer">
              {candidate.evidence.url}
            </Typography.Link>
          )}
        </Space>
      )}
    </div>
  );
}

/** Collapsed by default (mirrors CardImagesPanel's own toggle idiom) so a
 *  long rejection history doesn't crowd out the candidates still awaiting a
 *  decision — but never deleted from view, so a mis-click is always
 *  "recoverable by eye" one click away. No further actions: there is no
 *  un-reject affordance in this iteration. */
function RejectedGroup({ candidates }: { candidates: EnrichmentCandidate[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Button type="text" size="small" onClick={() => setOpen((v) => !v)}>
        {ENRICHMENT_COPY.rejectedGroup} ({candidates.length})
      </Button>
      {open && (
        <Space direction="vertical" size={4} style={{ width: '100%', paddingLeft: space.md }}>
          {candidates.map((c) => (
            <Space key={c.id} size={4} wrap>
              <Text type="secondary">{c.value}</Text>
              {/* Round 2 (human-readable source tags, 2026-09-23): same
                  human/humanized label as the active row — a rejected
                  candidate's source key is no more "raw code identifier" than
                  an active one's. */}
              <Tag>{sourceChipLabel(c.source)}</Tag>
            </Space>
          ))}
        </Space>
      )}
    </div>
  );
}

/** The layers of the ladder, cheapest first, with the words the strip uses. */
const LAYERS: Array<{ key: string; label: string; verb: string; timed: boolean }> = [
  { key: 'capture', label: ENRICHMENT_COPY.layerCapture, verb: ENRICHMENT_COPY.levelVerbCapture, timed: false },
  { key: 'agent', label: ENRICHMENT_COPY.layerAgent, verb: ENRICHMENT_COPY.levelVerbAgent, timed: true },
  { key: 'apollo', label: ENRICHMENT_COPY.layerApollo, verb: ENRICHMENT_COPY.levelVerbApollo, timed: true },
];

function fieldsPhrase(fields: Array<'email' | 'phone'>): string {
  if (fields.length === 2) return 'email and phone';
  return fields[0] ?? '';
}

/**
 * Who ran a call, for a person to read. The backend stores the actor's email,
 * then their display name, and only as a last resort the bare Entra object
 * id — which is all that older rows hold. A GUID tells a human nothing, so it
 * reads as a generic label instead.
 */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function actorLabel(by: string): string {
  const trimmed = by.trim();
  return trimmed === '' || GUID.test(trimmed) ? ENRICHMENT_COPY.unknownActor : trimmed;
}

/** "3 hours ago" / "just now" — the absolute time rides in a tooltip. */
function timeAgo(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return ENRICHMENT_COPY.justNow;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (seconds < 3600) return rtf.format(-Math.round(seconds / 60), 'minute');
  if (seconds < 86_400) return rtf.format(-Math.round(seconds / 3600), 'hour');
  return rtf.format(-Math.round(seconds / 86_400), 'day');
}

/** The one sentence for a layer's outcome — shared by the strip and the history. */
function levelText(
  layer: { verb: string; timed: boolean },
  level: EnrichmentRunLevel,
): { text: string; badge: 'success' | 'warning' | 'error' | 'default' | 'processing'; pages?: string[] } {
  if (level.status === 'skipped') {
    return {
      text: ENRICHMENT_COPY.levelSkipped[level.reason] ?? ENRICHMENT_COPY.levelSkippedFallback,
      badge: 'default',
    };
  }
  if (level.status === 'failed') {
    // Say WHY when the backend knows: a busy service is a "try again", not a dead end.
    const why = level.cause && level.cause !== 'error' ? ENRICHMENT_COPY.levelFailedCause[level.cause] : undefined;
    return {
      text: level.reason === 'thin_identity'
        ? ENRICHMENT_COPY.levelFailedThin
        : why ? `${ENRICHMENT_COPY.levelFailed} — ${why}` : ENRICHMENT_COPY.levelFailed,
      badge: 'error',
    };
  }
  const awaiting = level.awaiting ?? [];
  // Asked for something that arrives LATER: "nothing found" would be wrong, it is not in yet.
  const outcome =
    level.candidates > 0
      ? `${ENRICHMENT_COPY.levelFoundPrefix} ${fieldsPhrase(level.fields)}`
      : awaiting.length > 0 ? ENRICHMENT_COPY.levelNothingYet : ENRICHMENT_COPY.levelNothingFound;
  const extras = [
    layer.timed && level.durationMs >= 1000 ? ENRICHMENT_COPY.levelSeconds(level.durationMs) : null,
    level.searches ? ENRICHMENT_COPY.searchesMade(level.searches) : null,
    // Hostnames only for an older backend: a newer one sends `pages`, shown as links.
    !level.pages?.length && level.pagesRead && level.pagesRead.length > 0
      ? ENRICHMENT_COPY.pagesRead(level.pagesRead.join(', '))
      : null,
    level.needsReview ? ENRICHMENT_COPY.levelNeedsReview(level.needsReview) : null,
    level.dropped ? ENRICHMENT_COPY.levelDiscarded(level.dropped) : null,
    awaiting.length > 0 ? ENRICHMENT_COPY.levelAwaiting(fieldsPhrase(awaiting)) : null,
    level.budgetExhausted ? ENRICHMENT_COPY.levelRanOutOfTime : null,
  ].filter((x): x is string => x !== null);
  return {
    text: `${layer.verb}: ${outcome}${extras.length > 0 ? ` (${extras.join(' · ')})` : ''}`,
    badge: level.candidates > 0 ? 'success' : awaiting.length > 0 ? 'processing' : 'warning',
    ...(level.pages?.length ? { pages: level.pages } : {}),
  };
}

/**
 * How the employer's website was settled — and why — so an operator can judge
 * a machine-found domain instead of trusting it: the site, the page it was
 * judged on, the reason, every other candidate that was weighed (with a link
 * and why it was passed over), and where company addresses were seen.
 */
function DomainLine({ domain }: { domain: EnrichmentDomainOutcome }) {
  if (domain.status === 'unavailable') return null;
  const considered = domain.considered ?? [];
  const needsAttention = domain.status === 'not_found' || domain.status === 'resolved_weak';
  const badge: 'success' | 'warning' | 'default' =
    domain.status === 'resolved' || domain.status === 'agent' ? 'success' : domain.status === 'known' ? 'default' : 'warning';
  const label = domain.status === 'resolved_weak' ? ENRICHMENT_COPY.companySiteUnconfirmed : ENRICHMENT_COPY.companySite;
  return (
    <div data-testid="enrichment-domain">
      <Text>
        <Badge status={badge} /> {label} —{' '}
        {domain.status === 'not_found' ? (
          ENRICHMENT_COPY.domainNotFound
        ) : (
          <>
            <Text strong>{domain.host}</Text>
            {domain.status === 'known' && <> ({ENRICHMENT_COPY.domainKnown})</>}
            {domain.status === 'agent' && <> ({ENRICHMENT_COPY.domainFromAgent})</>}
            {domain.sourceUrl && isNavigableEvidenceUrl(domain.sourceUrl) && (
              <> · {ENRICHMENT_COPY.domainCheckedOn} <PageLink url={domain.sourceUrl} /></>
            )}
          </>
        )}
      </Text>
      {domain.reason && (
        <div>
          <Text type="secondary">{domain.reason}</Text>
        </div>
      )}
      {domain.emailDomainSeenAt && isNavigableEvidenceUrl(domain.emailDomainSeenAt) && (
        <div>
          <Text type="secondary">
            {ENRICHMENT_COPY.mailSeenOn(domain.emailDomain ?? domain.host ?? '')} <PageLink url={domain.emailDomainSeenAt} />
          </Text>
        </div>
      )}
      {considered.length > 0 && (
        <Collapse
          ghost
          size="small"
          defaultActiveKey={needsAttention ? ['weighed'] : []}
          items={[
            {
              key: 'weighed',
              label: ENRICHMENT_COPY.weighedTitle(considered.length),
              children: (
                <Space direction="vertical" size={2} data-testid="enrichment-domain-considered">
                  {considered.map((c) => (
                    <Text key={c.domain} type="secondary">
                      <Text strong={c.chosen}>{c.domain}</Text> <PageLink url={c.url} /> —{' '}
                      {c.chosen ? ENRICHMENT_COPY.weighedUsed : ENRICHMENT_COPY.weighedPassed}
                      {c.reason ? `: ${c.reason}` : ''}
                    </Text>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

/**
 * The company's own site, read for its main phone line when a phone was still
 * missing: the pages it actually read (links) and how many numbers it offered,
 * so "nothing found" is something the operator can check for themselves.
 */
function CompanyPhoneLine({ run }: { run: { found: number; checked: string[] } }) {
  return (
    <Text data-testid="enrichment-company-phone" type={run.found > 0 ? undefined : 'secondary'}>
      <Badge status={run.found > 0 ? 'success' : 'warning'} /> {ENRICHMENT_COPY.companyPhoneTitle} —{' '}
      {run.found > 0 ? ENRICHMENT_COPY.companyPhoneFound(run.found) : ENRICHMENT_COPY.companyPhoneNone}
      {run.checked.length > 0 && (
        <>
          {' '}· {ENRICHMENT_COPY.companyPhoneChecked}{' '}
          {run.checked.map((url, i) => (
            <span key={url}>
              {i > 0 && ', '}
              <PageLink url={url} />
            </span>
          ))}
        </>
      )}
    </Text>
  );
}

/**
 * "What ran": one line per layer for one enrich call. The console used to
 * show only candidates, so a layer that ran and found nothing — or was
 * skipped — looked the same as nothing happening at all. A layer missing
 * from `levels` is not turned on; every other outcome gets a sentence.
 * With a `run`, a header says when, by whom and how long, and a findings
 * line says what was wanted versus what came back.
 */
function WhatRan({ levels, run }: { levels: EnrichmentRunLevel[]; run?: EnrichmentRunHistoryEntry }) {
  const summary = run?.summary ?? null;
  const foundParts = summary
    ? ([
        summary.found.email > 0 ? 'email' : null,
        summary.found.phone > 0 ? 'phone' : null,
      ].filter((x): x is 'email' | 'phone' => x !== null))
    : [];
  const tags = ([
    summary?.explicit ? ENRICHMENT_COPY.explicitTag : null,
  ] as Array<string | null>).filter((x): x is string => x !== null);
  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }} data-testid="enrichment-what-ran">
      <Text strong>{ENRICHMENT_COPY.levelsTitle}</Text>
      {run && (
        <Text type="secondary" title={new Date(run.at).toLocaleString()}>
          {ENRICHMENT_COPY.lastRunPrefix} {ENRICHMENT_COPY.lastRunBy(timeAgo(run.at), actorLabel(run.by))}
          {summary ? ` · ${ENRICHMENT_COPY.lastRunTook(summary.durationMs)}` : ''}
          {tags.length > 0 ? ` · ${tags.join(', ')}` : ''}
        </Text>
      )}
      {summary && summary.wanted.length > 0 && (
        <Text type="secondary">
          {ENRICHMENT_COPY.wantedLine(fieldsPhrase(summary.wanted))} ·{' '}
          {foundParts.length > 0
            ? ENRICHMENT_COPY.foundLine(fieldsPhrase(foundParts))
            : ENRICHMENT_COPY.foundNone}
        </Text>
      )}
      {summary?.domain && <DomainLine domain={summary.domain} />}
      {summary?.companyPhone && <CompanyPhoneLine run={summary.companyPhone} />}
      {LAYERS.map((layer) => {
        const level = levels.find((l) => l.provider === layer.key);
        if (!level) {
          return (
            <Text key={layer.key} type="secondary">
              <Badge status="default" /> {layer.label} — {ENRICHMENT_COPY.levelNotOn}
            </Text>
          );
        }
        const { text, badge, pages } = levelText(layer, level);
        return (
          <Text key={layer.key} type={level.status === 'ran' ? undefined : 'secondary'}>
            <Badge status={badge} /> {layer.label} — {text}
            {pages && (
              <span data-testid="enrichment-pages-read">
                {' '}· {ENRICHMENT_COPY.pagesReadLabel}{' '}
                {pages.map((url, i) => (
                  <span key={url}>
                    {i > 0 && ', '}
                    <PageLink url={url} />
                  </span>
                ))}
              </span>
            )}
          </Text>
        );
      })}
    </Space>
  );
}

/** A past run as one compact line: when · who · what each layer returned. */
function runLine(run: EnrichmentRunHistoryEntry): string {
  const summary = run.summary;
  const head = `${timeAgo(run.at)} · ${actorLabel(run.by)}`;
  if (!summary) return head;
  if (summary.outcome === 'refused_thin_identity') return `${head} · ${ENRICHMENT_COPY.runOutcomeRefused}`;
  const layers = LAYERS.map((layer) => {
    const level = summary.levels.find((l) => l.provider === layer.key);
    if (!level || level.status === 'skipped') return null;
    if (level.status === 'failed') return `${layer.label}: ${ENRICHMENT_COPY.runOutcomeFailed}`;
    return `${layer.label}: ${
      level.candidates > 0 ? fieldsPhrase(level.fields) : ENRICHMENT_COPY.levelNothingFound
    }`;
  }).filter((x): x is string => x !== null);
  return layers.length > 0 ? `${head} · ${layers.join(' · ')}` : head;
}

/**
 * Contact-enrichment candidate review for a lead's email/phone. Driven
 * entirely by the injected `api` — this component never talks HTTP itself,
 * so it has no idea it's wired to the super-admin console specifically (the
 * customer portal simply never supplies `api.enrichment`, which is what
 * keeps this panel out of it — see LeadDetailBody).
 *
 * State the backend genuinely can't tell apart — "never enriched" vs "ran,
 * found nothing, no pending providers" both come back as empty
 * candidates/requests — is resolved locally: `foundNothing` only appears
 * once an Enrich click has completed in THIS session, while `stillLooking`
 * is read straight off `requests` so a pending async provider from an
 * earlier session is reflected immediately on load, with no click required.
 */
export function ContactEnrichmentPanel({ personId, api, onUseValue }: ContactEnrichmentPanelProps) {
  const queryClient = useQueryClient();
  const queryKey = ['contactEnrichment', personId];
  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => api.fetchEnrichment(personId),
    // A phone Apollo was asked to reveal arrives by webhook minutes later. While one
    // is pending, check back so it appears without a reload. Stops after
    // POLL_WINDOW_MS (a delivery that never comes expires server-side) and while
    // the tab is hidden (React Query's default).
    refetchInterval: (query) => (hasFreshPending(query.state.data) ? PENDING_POLL_MS : false),
  });

  const [enriching, setEnriching] = useState(false);
  const [attempted, setAttempted] = useState(false);
  // The last Enrich click's per-layer summary — component state, not query
  // data, so a background refetch of the plain read can't wipe it.
  const [lastRun, setLastRun] = useState<EnrichmentRunLevel[] | null>(null);
  const [actionResult, setActionResult] = useState<
    | null
    | { kind: 'thin'; message: string }
    | { kind: 'outOfCredits' }
    | { kind: 'apolloSkipped'; message: string }
  >(null);

  const candidates = data?.candidates ?? [];
  const requests = data?.requests ?? [];
  const person = data?.person ?? { email: '', phone: '' };
  const hasPending = requests.some((r) => r.status === 'pending');
  const runs = data?.runs ?? [];
  const latestRun = runs[0];
  const previousRuns = runs.slice(1);

  async function handleEnrich(opts?: { providers?: string[] }) {
    setEnriching(true);
    setAttempted(true);
    setActionResult(null);
    setLastRun(null);
    try {
      const next = await api.enrich(personId, opts);
      queryClient.setQueryData(queryKey, next);
      setLastRun(next.levels ?? null);
      // Not an error — a successful response can still mean "Apollo was
      // skipped", so it's checked here rather than in the catch below.
      // The go-live gates (2026-10-06) widened apolloSkipped from the one
      // ceiling value to a reason; unknown future values fall through
      // silently (additive contract).
      if (next.apolloSkipped === 'daily_ceiling') {
        setActionResult({ kind: 'outOfCredits' });
      } else if (!next.levels && next.apolloSkipped === 'no_domain') {
        setActionResult({ kind: 'apolloSkipped', message: ENRICHMENT_COPY.apolloNoDomain });
      } else if (!next.levels && next.apolloSkipped === 'phone_only') {
        setActionResult({ kind: 'apolloSkipped', message: ENRICHMENT_COPY.apolloPhoneOnly });
      }
    } catch (err: unknown) {
      const { status, message: msg } = errorStatus(err);
      // A failed run is not an empty run: the cheaper layers' results were
      // saved before the failure (the backend flushes first), so reload them
      // — otherwise a layer that DID find something looks like nothing
      // happened.
      void refetch();
      if (status === 422) {
        setActionResult({ kind: 'thin', message: msg ?? ENRICHMENT_COPY.couldNotLook });
      } else if (status === 429) {
        setActionResult({ kind: 'outOfCredits' });
      } else if (status === 503) {
        // Go-live gates: an Apollo outage arrives as a true 503 now. The
        // friendly copy, never the raw ENRICHMENT_FAILURE string.
        message.error(ENRICHMENT_COPY.couldNotLook);
      } else {
        message.error(msg ?? ENRICHMENT_COPY.couldNotLook);
      }
    } finally {
      setEnriching(false);
    }
  }

  async function handleReject(candidateId: string) {
    await api.rejectCandidate(personId, candidateId);
    await refetch();
  }

  const groups = FIELD_ORDER.filter((f) => candidates.some((c) => c.field === f)).map((field) => {
    const forField = candidates.filter((c) => c.field === field);
    return {
      field,
      active: forField.filter((c) => c.rejectedAt == null),
      rejected: forField.filter((c) => c.rejectedAt != null),
    };
  });

  // The Enrich trigger: one click, no confirmation. It runs every enabled
  // layer, Apollo included.
  const enrichLabel = enriching ? ENRICHMENT_COPY.researching : ENRICHMENT_COPY.enrichButton;
  const enrichTrigger = (
    <Button size="small" loading={enriching} onClick={() => handleEnrich()}>
      {enrichLabel}
    </Button>
  );

  // "Retry Apollo": an Apollo-only re-run, offered ONLY when Apollo did not get
  // to look because something went wrong — it failed, or an earlier step failed
  // before it was reached. Every other outcome is a decision, not a gap: it ran,
  // it was "not needed" (something else already found the value, or nothing is
  // missing), it had no domain to match on, or the daily limit was hit. Offering
  // a button after those only invites a click that changes nothing.
  const apolloLevel = (latestRun?.summary?.levels ?? lastRun)?.find((l) => l.provider === 'apollo');
  const apolloCanBeRetried =
    apolloLevel !== undefined
    && (apolloLevel.status === 'failed' || (apolloLevel.status === 'skipped' && apolloLevel.reason === 'not_reached'));

  return (
    <Card
      title={ENRICHMENT_COPY.title}
      size="small"
      extra={
        <Space size={space.xs}>
          {/* Re-run scoped to Apollo only, after it failed or was never reached. One click. */}
          {apolloCanBeRetried && (
            <Button size="small" loading={enriching} onClick={() => handleEnrich({ providers: ['apollo'] })}>
              {ENRICHMENT_COPY.retryApollo}
            </Button>
          )}
          {enrichTrigger}
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={space.sm}>
        {actionResult?.kind === 'thin' && <Alert type="warning" showIcon message={actionResult.message} />}
        {actionResult?.kind === 'apolloSkipped' && <Alert type="info" showIcon message={actionResult.message} />}
        {actionResult?.kind === 'outOfCredits' && (
          <Alert type="warning" showIcon message={ENRICHMENT_COPY.outOfCredits} />
        )}

        {/* While a run is in flight there is no answer yet: say so, and hide the
            PREVIOUS run's strip, which would otherwise read as this run's result. */}
        {enriching && (
          <Space size={space.xs} data-testid="enrichment-in-progress">
            <Spin size="small" />
            <Text type="secondary">{ENRICHMENT_COPY.inProgress}</Text>
          </Space>
        )}
        {!enriching && (latestRun?.summary?.levels ?? lastRun) && (
          <WhatRan levels={(latestRun?.summary?.levels ?? lastRun)!} run={latestRun?.summary ? latestRun : undefined} />
        )}
        {previousRuns.length > 0 && (
          <Collapse
            ghost
            size="small"
            items={[
              {
                key: 'previous-runs',
                label: ENRICHMENT_COPY.previousRuns(previousRuns.length),
                children: (
                  <Space direction="vertical" size={2} data-testid="enrichment-previous-runs">
                    {previousRuns.map((r) => (
                      <Text key={`${r.at}-${r.by}`} type="secondary" title={new Date(r.at).toLocaleString()}>
                        {runLine(r)}
                      </Text>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        )}

        {isLoading && !data ? (
          <Spin size="small" />
        ) : (
          <>
            {/* Still waiting on a delivery (Apollo reveals a phone by webhook, minutes later):
                say so whether or not other candidates are already listed. */}
            {hasPending && (
              <Space direction="vertical" size={0}>
                <Text type="secondary">{ENRICHMENT_COPY.stillLooking}</Text>
                <Text type="secondary">{ENRICHMENT_COPY.stillLookingHint}</Text>
              </Space>
            )}
            {candidates.length === 0 && !actionResult && (
              <>
                {/* Only once the request has actually come back empty: `attempted` is
                    set at the click, and a run can take a minute or more. */}
                {!hasPending && attempted && !enriching && <Text type="secondary">{ENRICHMENT_COPY.foundNothing}</Text>}
              </>
            )}

            {groups.map(({ field, active, rejected }) => (
              <div key={field}>
                <Text strong>{FIELD_LABEL[field]}</Text>
                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                  {active.map((c) => (
                    <CandidateRow
                      key={c.id}
                      candidate={c}
                      // fix/enrichment-matches-current: prefer the server's
                      // format-blind verdict; fall back to the old raw-string
                      // comparison only when an older backend never sent the
                      // field at all (deploy skew), so the panel stays
                      // correct either way.
                      isMatch={c.matchesCurrent ?? c.value === person[field]}
                      onUse={onUseValue}
                      onReject={handleReject}
                    />
                  ))}
                </Space>
                {rejected.length > 0 && <RejectedGroup candidates={rejected} />}
              </div>
            ))}
          </>
        )}
      </Space>
    </Card>
  );
}
